<template>
  <b-modal
    :id="modalId"
    :title="$t('order.route-sorting.modal.title')"
    modal-class="order-route-sort-modal"
    body-class="order-route-sort-modal__body"
    hide-footer
    @hidden="handleHidden"
  >
    <div class="order-route-sort__subtitle mb-1">
      {{ $t('order.route-sorting.modal.subtitle') }}
    </div>

    <rc-overlay :show="isSaving">
      <draggable
        :list="sortablePatients"
        item-key="id"
        :disabled="isSaving"
        ghost-class="order-route-sort__item--ghost"
        chosen-class="order-route-sort__item--chosen"
      >
        <div
          v-for="(patient, index) in sortablePatients"
          :key="patient.id || index"
          class="order-route-sort__item cursor-move"
        >
          <div class="order-route-sort__item-content">
            <div class="order-route-sort__patient-address">
              {{ getPatientAddressLabel(patient) }}
            </div>
            <div class="order-route-sort__patient-name">
              {{ getPatientLabel(patient, index) }}
            </div>
          </div>
        </div>
      </draggable>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="isSaving"
          @click="close"
        >
          {{ $t('shared.button.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="isSaving || sortablePatients.length < 2 || !hasRouteOrderChanged"
          @click="handleSave"
        >
          {{ $t('shared.button.submit') }}
        </b-button>
      </div>
    </rc-overlay>
  </b-modal>
</template>

<script>
import draggable from 'vuedraggable'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import { parseRequestError } from '@/helpers/helpers'
import { isPatientVisibleInView } from '@/helpers/patient'

export default {
  name: 'OrderManualRouteSortModal',
  components: {
    draggable,
    RcOverlay,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      modalId: 'order-manual-route-sort-modal',
      isSaving: false,
      sortablePatients: [],
    }
  },
  computed: {
    visiblePatientIds() {
      return this.getVisiblePatients()
        .map(patient => patient?.id)
        .filter(Boolean)
    },
    sortablePatientIds() {
      return this.sortablePatients
        .map(patient => patient?.id)
        .filter(Boolean)
    },
    hasRouteOrderChanged() {
      if (this.visiblePatientIds.length !== this.sortablePatientIds.length) {
        return false
      }

      return this.visiblePatientIds.some((patientId, index) => patientId !== this.sortablePatientIds[index])
    },
  },
  methods: {
    open() {
      this.sortablePatients = this.getVisiblePatients().map(patient => ({ ...patient }))
      this.$nextTick(() => {
        this.$bvModal.show(this.modalId)
      })
    },
    close() {
      this.$bvModal.hide(this.modalId)
    },
    handleHidden() {
      this.isSaving = false
      this.sortablePatients = []
    },
    getVisiblePatients() {
      if (!Array.isArray(this.order?.patientInfo)) {
        return []
      }

      return this.order.patientInfo.filter(patient => isPatientVisibleInView(patient))
    },
    getPatientLabel(patient, index) {
      const fullName = [patient?.surname, patient?.name].filter(Boolean).join(' ')
      if (fullName) {
        return fullName
      }

      return this.$t('order.patient-info.single-title', { index: index + 1 })
    },
    getPatientAddressLabel(patient) {
      const address = patient?.address
      if (!address || typeof address !== 'object') {
        return this.$t('shared.no-data')
      }

      const zipCity = [address.zip, address.city].filter(Boolean).join(' ')
      return [address.name, address.address, zipCity].filter(Boolean).join(', ')
    },
    handleSave() {
      if (this.isSaving || !this.order?.id || this.sortablePatients.length < 2 || !this.hasRouteOrderChanged) {
        return
      }

      this.isSaving = true
      this.$store.dispatch('order/manualSortOrderRoute', {
        orderId: this.order.id,
        patientInfoIds: this.sortablePatients.map(patient => patient.id).filter(Boolean),
      })
        .then(response => {
          const routeOrderChanged = !!response?.data?.changed
          const notifyKey = routeOrderChanged
            ? 'order.route-sorting.notify.success'
            : 'order.route-sorting.notify.no-changes'

          window.toast.notify.success(this.$t(notifyKey))
          this.$emit('sorted')
          this.close()
        })
        .catch(error => {
          parseRequestError(error)
        })
        .finally(() => {
          this.isSaving = false
        })
    },
  },
}
</script>

<style lang="scss">
.order-route-sort-modal .modal-content {
  background: #fff;
  border: 1px solid #ebe9f1;
}

.order-route-sort-modal .modal-header {
  background: #fff;
  border-bottom: 1px solid #ebe9f1;
}

.order-route-sort-modal .modal-header .modal-title {
  color: #5e5873;
}

.order-route-sort-modal__body {
  background: #fff;
}

.order-route-sort__subtitle {
  color: #6e6b7b;
}

.order-route-sort__item {
  padding: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  background: #fff;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;

  & + & {
    margin-top: 0.75rem;
  }

  &:hover {
    background: #f8f8f8;
    border-color: #7367f0;
  }
}

.order-route-sort__item-content {
  min-width: 0;
}

.order-route-sort__patient-name {
  color: #5e5873;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}

.order-route-sort__patient-address {
  margin-top: 0.375rem;
  color: #6e6b7b;
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
}

.order-route-sort__item--ghost {
  opacity: 0.45;
}

.order-route-sort__item--chosen {
  transform: scale(1.01);
}

.dark-layout {
  .order-route-sort-modal .modal-content {
    background: #283046;
    border-color: rgba(92, 111, 168, 0.45);
  }

  .order-route-sort-modal .modal-header {
    background: #161d31;
    border-bottom-color: rgba(92, 111, 168, 0.3);
  }

  .order-route-sort-modal .modal-header .modal-title {
    color: #f4f7ff;
  }

  .order-route-sort-modal__body {
    background: #283046;
  }

  .order-route-sort__subtitle {
    color: rgba(215, 222, 255, 0.72);
  }

  .order-route-sort__item {
    border-color: rgba(92, 111, 168, 0.45);
    background: rgba(32, 43, 78, 0.92);
    box-shadow: 0 0.5rem 1rem rgba(9, 16, 36, 0.18);

    &:hover {
      background: rgba(38, 51, 92, 0.98);
      border-color: rgba(122, 145, 214, 0.65);
    }
  }

  .order-route-sort__patient-name {
    color: #f4f7ff;
  }

  .order-route-sort__patient-address {
    color: rgba(215, 222, 255, 0.82);
  }
}
</style>
